/**
 * FAQ Widget Frontend Handler.
 *
 * Handles:
 * - Scoped live search filtering with instant clear button
 * - Category tab filtering for group rows and items
 * - Accessible accordion toggling (with multiple vs single open support)
 * - Empty search state handling & reset button
 * - Elementor element_ready and late-render MutationObserver sweep
 *
 * @package MyProPlugins
 */

(() => {
	'use strict';

	const INITIALIZED_ATTR = 'data-mypro-faq-initialized';

	function initFaqWidget(root) {
		if (!root || root.hasAttribute(INITIALIZED_ATTR)) {
			return;
		}
		root.setAttribute(INITIALIZED_ATTR, 'true');

		const allowMultiple = root.getAttribute('data-allow-multiple') !== 'false';
		const searchInput = root.querySelector('.js-faq-search-input');
		const searchClearBtn = root.querySelector('.js-faq-search-clear');
		const categoryCards = root.querySelectorAll('.js-faq-category-btn');
		const groupRows = root.querySelectorAll('.js-faq-group-row');
		const flatList = root.querySelector('.js-faq-flat-list');
		const allItems = root.querySelectorAll('.js-faq-item');
		const emptyState = root.querySelector('.js-faq-empty-state');
		const resetFilterBtn = root.querySelector('.js-faq-empty-reset-btn');

		let currentCategory = 'all';
		let searchQuery = '';

		// 1. Accordion Toggle Logic
		allItems.forEach((item) => {
			const btn = item.querySelector('.js-faq-question-btn');
			const answer = item.querySelector('.js-faq-answer');
			if (!btn || !answer) return;

			btn.addEventListener('click', () => {
				const isOpen = item.classList.contains('is-open');

				if (!allowMultiple && !isOpen) {
					// Close other items in the same column/row or whole widget
					const parentColumn = item.closest('.faq-accordion-column') || root;
					parentColumn.querySelectorAll('.js-faq-item.is-open').forEach((otherItem) => {
						if (otherItem !== item) {
							otherItem.classList.remove('is-open');
							const otherBtn = otherItem.querySelector('.js-faq-question-btn');
							if (otherBtn) {
								otherBtn.setAttribute('aria-expanded', 'false');
							}
						}
					});
				}

				item.classList.toggle('is-open', !isOpen);
				btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
			});
		});

		// 2. Filter and Search Engine
		function updateFilter() {
			let totalVisibleItems = 0;
			const query = searchQuery.toLowerCase().trim();

			// Toggle clear button
			if (searchClearBtn) {
				if (query.length > 0) {
					searchClearBtn.classList.add('is-visible');
				} else {
					searchClearBtn.classList.remove('is-visible');
				}
			}

			// Group rows handling
			if (groupRows && groupRows.length > 0) {
				groupRows.forEach((groupRow) => {
					const groupCategory = groupRow.getAttribute('data-group-category') || '';
					const matchesCategoryGroup =
						currentCategory === 'all' ||
						groupCategory === currentCategory ||
						(currentCategory === 'tentang-mypro' && groupCategory === 'all');

					let visibleInGroup = 0;
					const itemsInGroup = groupRow.querySelectorAll('.js-faq-item');

					itemsInGroup.forEach((item) => {
						const itemCategory = item.getAttribute('data-category') || '';
						const text = (item.textContent || '').toLowerCase();

						const matchesCategory =
							currentCategory === 'all' ||
							itemCategory === currentCategory ||
							(currentCategory === 'all' && (itemCategory === 'tentang-mypro' || itemCategory === 'all'));
						const matchesSearch = !query || text.includes(query);

						if (matchesCategory && matchesSearch) {
							item.style.display = 'block';
							visibleInGroup++;
							totalVisibleItems++;
						} else {
							item.style.display = 'none';
						}
					});

					if (matchesCategoryGroup && visibleInGroup > 0) {
						groupRow.style.display = 'grid';
					} else {
						groupRow.style.display = 'none';
					}
				});
			} else if (flatList) {
				// Flat list handling
				allItems.forEach((item) => {
					const itemCategory = item.getAttribute('data-category') || '';
					const text = (item.textContent || '').toLowerCase();

					const matchesCategory =
						currentCategory === 'all' ||
						itemCategory === currentCategory ||
						(currentCategory === 'tentang-mypro' && itemCategory === 'all');
					const matchesSearch = !query || text.includes(query);

					if (matchesCategory && matchesSearch) {
						item.style.display = 'block';
						totalVisibleItems++;
					} else {
						item.style.display = 'none';
					}
				});
			}

			// Empty state toggle
			if (emptyState) {
				if (totalVisibleItems === 0) {
					emptyState.classList.add('is-visible');
				} else {
					emptyState.classList.remove('is-visible');
				}
			}
		}

		// 3. Category Buttons
		categoryCards.forEach((card) => {
			card.addEventListener('click', () => {
				categoryCards.forEach((c) => {
					c.classList.remove('is-active');
					c.setAttribute('aria-selected', 'false');
				});
				card.classList.add('is-active');
				card.setAttribute('aria-selected', 'true');
				currentCategory = card.getAttribute('data-category') || 'all';
				updateFilter();
			});
		});

		// 4. Search Input
		if (searchInput) {
			searchInput.addEventListener('input', (e) => {
				searchQuery = e.target.value;
				updateFilter();
			});
		}

		// 5. Clear Search Button
		if (searchClearBtn && searchInput) {
			searchClearBtn.addEventListener('click', () => {
				searchInput.value = '';
				searchQuery = '';
				searchInput.focus();
				updateFilter();
			});
		}

		// 6. Reset Empty State Button
		if (resetFilterBtn) {
			resetFilterBtn.addEventListener('click', () => {
				currentCategory = 'all';
				searchQuery = '';
				if (searchInput) searchInput.value = '';
				categoryCards.forEach((c) => {
					const cat = c.getAttribute('data-category');
					if (cat === 'all' || cat === 'tentang-mypro') {
						c.classList.add('is-active');
						c.setAttribute('aria-selected', 'true');
					} else {
						c.classList.remove('is-active');
						c.setAttribute('aria-selected', 'false');
					}
				});
				updateFilter();
			});
		}

		// Initial filter pass
		updateFilter();
	}

	function initAll() {
		const roots = document.querySelectorAll('.mypro-faq');
		roots.forEach(initFaqWidget);
	}

	// Elementor hook integration
	function registerElementReady() {
		if (window.elementorFrontend && window.elementorFrontend.hooks) {
			window.elementorFrontend.hooks.addAction('frontend/element_ready/mypro_faq.default', ($scope) => {
				const el = $scope[0] ? $scope[0].querySelector('.mypro-faq') || $scope[0] : null;
				if (el) initFaqWidget(el);
			});
			window.elementorFrontend.hooks.addAction('frontend/element_ready/mypro_faq', ($scope) => {
				const el = $scope[0] ? $scope[0].querySelector('.mypro-faq') || $scope[0] : null;
				if (el) initFaqWidget(el);
			});
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			initAll();
			registerElementReady();
		});
	} else {
		initAll();
		registerElementReady();
	}

	window.addEventListener('elementor/frontend/init', registerElementReady);

	// MutationObserver fallback for editor changes and late rendering
	let sweepQueued = false;
	function queueSweep() {
		if (sweepQueued) return;
		sweepQueued = true;
		requestAnimationFrame(() => {
			sweepQueued = false;
			initAll();
		});
	}

	if (typeof MutationObserver !== 'undefined') {
		new MutationObserver(queueSweep).observe(document.documentElement, {
			childList: true,
			subtree: true,
		});
	}
})();
